/**
 * SelectList — renders the SELECT clause's column list. In RO mode every
 * affordance is disabled (drag handle / × / [+ add column]). When VQE-Edit
 * supplies an `editContext` prop, the [+ add column] popover is populated by
 * `useColumnSuggestions` and the per-row `×` button commits a remove edit.
 *
 * Subquery wiring is unchanged from VQE-RO: an inline `(SELECT … )` in a
 * select-list item is rendered via `renderInlineSubqueries`.
 */

import { useMemo, type ReactNode } from 'react';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import type { ParseOptions } from '../../../../sql-grammar/model';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { classifySelectItems } from '../../detection/aggregateAwareness';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext, toCommitCtx } from '../../edit/editContext';
import { runSequentialStatementEdits } from '../../edit/runSequentialStatementEdits';
import { addColumn } from '../../edit/edits/addColumn';
import { removeColumn } from '../../edit/edits/removeColumn';
import { reorderColumns } from '../../edit/edits/reorderColumns';
import { toggleSelectDistinct } from '../../edit/edits/toggleSelectDistinct';
import { buildDrillLabel, extractPredicateColumn } from '../../ux/drillLabel';
import { vqeCopy } from '../../ux/vqeCopy';
import { CommandPickerPopover } from '../common/CommandPickerPopover';
import { inScopeRelationPrefixes, shouldFlagOutOfScope } from '../common/inScopeRelations';
import { TogglePill } from '../common/TogglePill';
import { useInScopeColumnPicker } from '../common/useInScopeColumnPicker';
import { SelectColumnChip, ColumnLabel } from './SelectColumnChip';
import { ClauseCard } from './ClauseCard';
import { SelectListAggregateSectionPrompt } from './SelectListAggregateSectionPrompt';
import { SelectListAggregateWarning } from './SelectListAggregateWarning';
import { SelectStarExpand } from './SelectStarExpand';
import { isBareStar } from '../../edit/edits/expandSelectStar';
import type { SelectOutline } from './extractSelectOutline';
import { extractSubqueries } from './extractSubqueries';
import { detectOverInItem, extractWindowFunction } from './extractWindowFunction';
import { parseSelectItem } from './parseSelectItem';
import { renderInlineSubqueries } from './renderInlineSubqueries';
import type { SubqueryWiring } from './SelectRenderer';
import { splitTopLevelCommas } from './splitTopLevel';
import { WindowSpec } from './WindowSpec';

export interface SelectListProps {
  readonly bufferText: string;
  readonly outline: SelectOutline;
  readonly options: ParseOptions;
  readonly subqueryCtx?: SubqueryWiring;
  readonly statementId?: string;
  readonly editContext?: VqeEditContext;
  /**
   * Compact layout (compact popover + inline subqueries): the [+ Add] affordance
   * sits inline at the end of the chip row instead of as a footer below the list,
   * to save vertical space. The full editor keeps the footer button.
   */
  readonly compact?: boolean;
  /**
   * ANA-13/17/19 — compact-surface per-column window degrade. When true, any
   * SELECT item that is a window function renders READ-ONLY (via `WindowSpec`'s
   * built-in read-only path: opaque OVER body + disabled edit affordance) and is
   * frozen (not sortable / not removable), while every NON-window chip stays
   * fully editable. The compact popover has no structured OVER editor, so a
   * window column can't be faithfully edited there — locking just that chip
   * (instead of degrading the whole query) keeps FROM / WHERE / other columns
   * editable. Threaded ONLY from `CompactEditSections`; NEVER gate on `compact`,
   * which is ALSO true for full-editor nested/inline subqueries whose window
   * chips must stay editable.
   */
  readonly lockWindowColumns?: boolean;
  /**
   * Optional control rendered on the right of the "Select" card header. Used by
   * the compact inline-subquery path to host the kebab Add-CTE menu (F3) — it
   * sits alongside the DISTINCT badge / aggregate prompt in the accessory slot.
   */
  readonly headerMenu?: ReactNode;
}

export function SelectList({
  bufferText,
  outline,
  options,
  subqueryCtx,
  statementId,
  editContext,
  compact,
  lockWindowColumns,
  headerMenu,
}: SelectListProps) {
  const span = outline.selectList;
  const items = span
    ? splitTopLevelCommas(bufferText, span.start, span.end, options).map((item) => ({
        item,
        parsed: parseSelectItem(bufferText, item.start, item.end, options),
      }))
    : [];

  // Edit affordances only light up once the resolver service has a schema
  // snapshot for the tab's connection. Without a snapshot, suggestions are
  // empty and the popover would be useless; gating the × button on the same
  // signal keeps the VQE-RO scaffolding contract intact for tabs opened
  // without a connection.
  const editReady =
    !!editContext && !!editContext.service.getSchemaSnapshot();

  // Aggregate-awareness: when the SELECT mixes aggregates with non-aggregates,
  // surface a warning icon next to each non-aggregate item that would need to
  // appear in GROUP BY for the query to be valid. Click → addGroupBy(text).
  // Only computed when editing is wired so the VQE-RO scaffolding stays
  // unchanged for tabs without a connection.
  const aggClassifications = useMemo(() => {
    if (!editReady || !editContext) return null;
    try {
      return classifySelectItems(extractStructureForContext(editContext));
    } catch {
      return null;
    }
    // bufferText drives recomputation when the underlying buffer changes —
    // the classifier reads from the resolver service's parsed buffer cache,
    // so the dep is needed to stay in sync with edits committed elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editReady, editContext, bufferText]);

  // Folded aliases + table names actually in the query's FROM/JOIN. Drives the
  // out-of-scope red marking on SELECT items that reference a table the query
  // doesn't include. Empty when we can't confidently resolve the FROM set, in
  // which case `shouldFlagOutOfScope` flags nothing.
  const inScopePrefixes = useMemo(() => {
    if (!editReady || !editContext) return new Set<string>();
    try {
      return inScopeRelationPrefixes(extractStructureForContext(editContext));
    } catch {
      return new Set<string>();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editReady, editContext, bufferText]);

  const { track } = useAnalytics();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !editContext) return;
    const from = Number(active.id);
    const to = Number(over.id);
    if (!Number.isInteger(from) || !Number.isInteger(to)) return;
    void reorderColumns(from, to, toCommitCtx(editContext));
    track('feature:vqe_reorder_columns', { target: 'select' });
  };

  // The live toggle only lights up once editing is actually ready (schema
  // snapshot present) — mirrors the `editReady` gate used by the remove
  // buttons above. With an editContext present but no snapshot yet (or no
  // editContext at all), DISTINCT falls back to the pre-Task-3 read-only
  // span so a disconnected tab never shows a clickable-looking control.
  //
  // TogglePill (Wave 0 primitive #7): a toggle chip like ORDER BY's DESC —
  // outlined when off, filled when on (`aria-pressed`) — rather than a small
  // checkbox glyph that read faintly in the dark theme (SEL-13/SEL-19).
  const distinctBadge = editReady && editContext ? (
    <TogglePill
      testId="vqe-select-distinct-badge"
      ariaLabel={outline.distinct ? 'DISTINCT is on — click to turn off' : 'DISTINCT is off — click to turn on'}
      pressed={outline.distinct}
      tooltip={vqeCopy.hint.distinctToggle(outline.distinct)}
      onClick={() => {
        const next = !outline.distinct;
        void toggleSelectDistinct(next, toCommitCtx(editContext));
        track('feature:vqe_toggle_distinct', { enabled: next });
      }}
    >
      DISTINCT
    </TogglePill>
  ) : outline.distinct ? (
    <span
      className="rounded bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
      data-testid="vqe-select-distinct-badge"
    >
      DISTINCT
    </span>
  ) : null;

  // Non-aggregate columns flagged as not-in-GROUP-BY. The per-row warning icons
  // (one click each) remain; this list powers the section-level "Fix all" prompt
  // in the card header, which adds every missing column in a single click.
  const missingGroupByColumns = (aggClassifications ?? [])
    .filter((c) => c.needsGroupBy)
    .map((c) => c.columnText);

  const accessory =
    headerMenu || (editContext && missingGroupByColumns.length > 0) ? (
      <div className="flex items-center gap-2">
        {editContext && missingGroupByColumns.length > 0 ? (
          <SelectListAggregateSectionPrompt
            editContext={editContext}
            missingColumns={missingGroupByColumns}
          />
        ) : null}
        {headerMenu}
      </div>
    ) : null;

  const addColumnNode = (
    <SelectListAddColumn editContext={editContext} itemCount={items.length} />
  );

  // A lone bare `*` gets a "Choose columns" affordance: expanding it is the
  // only way the column chooser can act on a starred SELECT (there is no
  // per-column chip to remove until the star is exploded). While it shows it is
  // the ONLY add action — "Add column" next to it would append to `*` — and
  // "Add column" returns once the list is explicit (both surfaces).
  const starItem = items.length === 1 && isBareStar(items[0]!.parsed.expression) ? items[0]!.item : null;
  const starExpandNode =
    starItem && editReady && editContext ? (
      <SelectStarExpand editContext={editContext} bufferText={bufferText} starSpan={starItem} />
    ) : null;
  // DISTINCT changes the query, so it sits with the add action — after it —
  // rather than at the row's end, where the view-only controls go.
  const addActionNode = (
    <div className="flex flex-wrap items-center gap-2">
      {starExpandNode ?? addColumnNode}
      {distinctBadge}
    </div>
  );

  return (
    <ClauseCard
      title="Select"
      testId="vqe-select-list"
      accessory={accessory}
      density="line"
      // Full layout: the add action follows the chips on the title's line when it fits.
      footer={compact ? undefined : addActionNode}
    >
      {items.length === 0 ? (
        compact ? (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="vqe-select-list-items">
            {addActionNode}
          </div>
        ) : (
          <p className="text-xs italic text-muted-foreground" data-testid="vqe-select-list-empty">
            No columns parsed.
          </p>
        )
      ) : (() => {
        const chipNodes = items.map((entry, idx) => {
          const { item, parsed } = entry;
          const windowItem = detectOverInItem(bufferText, item.start, item.end, options)
            ? extractWindowFunction(bufferText, item.start, item.end, options)
            : null;
          const refs =
            subqueryCtx && !windowItem
              ? extractSubqueries(bufferText, item.start, item.end, options)
              : [];
          const hasSub = refs.length > 0;
          const selectColLabel = buildDrillLabel({
            kind: 'select-col',
            identifier: parsed.alias ?? extractPredicateColumn(parsed.expression),
            positionalIndex: idx,
          });
          const inline =
            subqueryCtx && hasSub
              ? renderInlineSubqueries({
                  bufferText,
                  start: item.start,
                  end: item.end,
                  options,
                  subqueryCtx,
                  testIdPrefix: `vqe-select-list-item-${idx}`,
                  drillLabelPrefix: selectColLabel,
                })
              : null;
          const renderedAlias = windowItem ? windowItem.alias : parsed.alias;
          const outOfScope =
            !windowItem && !inline && shouldFlagOutOfScope(parsed.expression, inScopePrefixes);

          // ANA-13/17/19: a locked window chip renders through WindowSpec's
          // read-only path by withholding the editContext — the raw OVER text
          // stays untouched (no re-emit), and the chip is frozen below.
          const windowLocked = !!windowItem && !!lockWindowColumns;
          const content = windowItem ? (
            <WindowSpec
              item={windowItem}
              statementId={statementId ?? subqueryCtx?.statementId ?? 'window'}
              itemIndex={idx}
              testIdPrefix={`vqe-select-list-window-${idx}`}
              options={options}
              editContext={windowLocked ? undefined : editContext}
            />
          ) : inline ? (
            <span
              // `min-w-0`: without it this flex item's automatic minimum is
              // its (nowrap) content, so a wide inline subquery pushes the
              // chip's alias pill + remove × outside the chip border.
              className="flex min-w-0 flex-wrap items-center gap-1"
              data-testid={`vqe-select-list-expr-${idx}`}
            >
              {inline.nodes}
            </span>
          ) : (
            <ColumnLabel
              expression={parsed.expression}
              outOfScope={outOfScope}
              testId={`vqe-select-list-expr-${idx}`}
            />
          );

          const extra =
            aggClassifications?.[idx]?.needsGroupBy && editContext ? (
              <SelectListAggregateWarning
                editContext={editContext}
                columnText={aggClassifications[idx].columnText}
                index={idx}
              />
            ) : null;

          return (
            <SelectColumnChip
              key={`${parsed.expression}-${idx}`}
              id={String(idx)}
              index={idx}
              content={content}
              alias={renderedAlias}
              outOfScope={outOfScope}
              sortable={editReady && !windowLocked}
              removable={editReady && !windowLocked}
              hasSubquery={hasSub}
              hasWindow={!!windowItem}
              extra={extra}
              onRemove={
                editReady && editContext && !windowLocked
                  ? () => {
                      void removeColumn(idx, toCommitCtx(editContext));
                    }
                  : undefined
              }
            />
          );
        });

        // The chip flex-row (`vqe-select-list-items`) holds ONLY the sortable
        // chips — `addColumnNode` used to be appended as a trailing sibling
        // inside it, so when chips wrapped to a second row the [+ Add] button
        // rode along the end of that row and read as attached to the last
        // chip (ANA-23). In compact mode it's lifted into its own row below
        // the chips so it reads as a SELECT-list-level action instead.
        const itemsRow = editReady ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((_, idx) => String(idx))} strategy={rectSortingStrategy}>
              <div className="flex flex-wrap items-center gap-1.5" data-testid="vqe-select-list-items">
                {chipNodes}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="vqe-select-list-items">
            {chipNodes}
          </div>
        );

        if (!compact) return itemsRow;

        return (
          <div className="flex flex-col gap-1.5">
            {itemsRow}
            <div className="flex items-center gap-1" data-testid="vqe-select-add-column-row">
              {addActionNode}
            </div>
          </div>
        );
      })()}
    </ClauseCard>
  );
}

interface SelectListAddColumnProps {
  readonly editContext?: VqeEditContext;
  readonly itemCount: number;
}

export function SelectListAddColumn({ editContext, itemCount }: SelectListAddColumnProps) {
  // Same "Add column" label on both surfaces (the compact popover used a bare
  // "Add", which read ambiguously next to "Choose columns").
  const triggerLabel = vqeCopy.actions.addColumn;
  if (!editContext) {
    // RO mode — render the disabled scaffolding without engaging TanStack
    // Query so VQE-RO unit tests don't need a QueryClientProvider.
    return (
      <CommandPickerPopover
        triggerLabel={triggerLabel}
        testId="vqe-select-list-add-column"
      />
    );
  }
  return <SelectListAddColumnEnabled editContext={editContext} itemCount={itemCount} triggerLabel={triggerLabel} />;
}

interface EnabledProps {
  readonly editContext: VqeEditContext;
  readonly itemCount: number;
  readonly triggerLabel: string;
}

function SelectListAddColumnEnabled({ editContext, itemCount, triggerLabel }: EnabledProps) {
  const { track } = useAnalytics();
  const picker = useInScopeColumnPicker(
    editContext,
    `${editContext.documentId}::${editContext.statementId}`,
    { selection: 'select' },
  );

  const onSelect = (key: string) => {
    // A checked row is already in the SELECT list — picking it removes it.
    const selectedIndex = picker.selectedIndexOf(key);
    if (selectedIndex !== undefined) {
      void removeColumn(selectedIndex, toCommitCtx(editContext));
      track('feature:vqe_picker_uncheck', { target: 'select' });
      return;
    }
    const col = picker.getColumn(key);
    if (!col) return;
    void addColumn(col, toCommitCtx(editContext), {
      refs: picker.refs,
      afterIndex: itemCount > 0 ? itemCount - 1 : undefined,
    });
  };

  const onGroupAction = (relationFolded: string) => {
    const groupCols = picker.getUnselectedColumnsForRelation(relationFolded);
    if (groupCols.length === 0) return;
    void runSequentialStatementEdits(editContext, groupCols, (c, ctx) =>
      addColumn(c, ctx, { refs: picker.refs }),
    );
    track('feature:vqe_add_all_columns', { count: groupCols.length, target: 'select' });
  };

  return (
    <CommandPickerPopover
      triggerLabel={triggerLabel}
      testId="vqe-select-list-add-column"
      disabled={!picker.enabled}
      tooltip={picker.enabled ? 'Add a column to the SELECT list' : undefined}
      searchPlaceholder="Search columns…"
      groups={picker.groups}
      onSelect={picker.enabled ? onSelect : undefined}
      closeOnSelect
      onGroupAction={picker.enabled ? onGroupAction : undefined}
      scopeToggle={picker.scopeToggle}
      loading={picker.isLoading}
      notice={picker.notice}
      emptyMessage={
        picker.emptyMessage ??
        (picker.hasSnapshot
          ? 'No columns in scope. Connect to a database with the relevant tables in scope.'
          : 'No schema cache yet — connect to a database to populate suggestions.')
      }
    />
  );
}
